import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import AppNav from "@/components/site/AppNav";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { User, Lock, CreditCard, Receipt, Loader2, ExternalLink, Eye, EyeOff, Check, Camera, Trash2 } from "lucide-react";
import UserAvatar from "@/components/site/UserAvatar";

const TABS = [
  { id: "profile", label: "Profile", icon: User },
  { id: "password", label: "Password", icon: Lock },
  { id: "billing", label: "Billing details", icon: CreditCard },
  { id: "transactions", label: "Transactions", icon: Receipt },
];

function fmtAmount(cents, currency = "usd") {
  const amount = (Number(cents || 0) / 100).toFixed(2);
  const cur = (currency || "usd").toUpperCase();
  return `${cur === "USD" ? "$" : ""}${amount}${cur !== "USD" ? " " + cur : ""}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function StatusPill({ status, paymentStatus }) {
  const s = paymentStatus === "paid" ? "paid" : status || paymentStatus || "pending";
  const map = {
    paid: "text-emerald-600 border-emerald-600/40 bg-emerald-500/10",
    initiated: "text-amber-600 border-amber-600/40 bg-amber-500/10",
    open: "text-amber-600 border-amber-600/40 bg-amber-500/10",
    complete: "text-emerald-600 border-emerald-600/40 bg-emerald-500/10",
    expired: "text-muted-foreground border-border bg-muted/30",
    canceled: "text-muted-foreground border-border bg-muted/30",
  };
  const cls = map[s] || "text-muted-foreground border-border bg-muted/30";
  return (
    <span
      className={`overline text-[10px] px-2 py-0.5 border rounded-none ${cls}`}
      data-testid={`txn-status-${s}`}
    >
      {s}
    </span>
  );
}

export default function Account() {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") || "profile";
  const [tab, setTab] = useState(TABS.find((t) => t.id === initialTab) ? initialTab : "profile");

  useEffect(() => {
    const cur = params.get("tab");
    if (cur !== tab) {
      const next = new URLSearchParams(params);
      next.set("tab", tab);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <div className="min-h-screen bg-background" data-testid="account-page">
      <AppNav />
      <main className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 overflow-x-hidden">
        <div className="border-b border-border pb-8 mb-10">
          <div className="overline text-primary mb-3">My account</div>
          <h1 className="font-serif-display text-4xl md:text-5xl leading-tight tracking-tight">
            Your details.<br />
            <span className="text-primary">Your control.</span>
          </h1>
          <p className="mt-4 text-muted-foreground max-w-2xl">
            Update your profile, change your password, manage payment methods and invoices, and review
            every transaction on your Draftwave account.
          </p>
        </div>

        <div className="grid md:grid-cols-[220px_1fr] gap-10">
          <aside className="md:sticky md:top-24 md:self-start min-w-0">
            <ul className="border border-border divide-y divide-border" data-testid="account-tabs">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = tab === t.id;
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition hover-invert ${
                        active ? "bg-foreground text-background" : "text-foreground"
                      }`}
                      onClick={() => setTab(t.id)}
                      data-testid={`account-tab-${t.id}`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="overline text-xs">{t.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="mt-6 border border-border p-4 text-xs text-muted-foreground">
              <div className="overline mb-2">Signed in as</div>
              <div className="text-foreground font-medium break-all" data-testid="account-email">
                {user?.email}
              </div>
              <div className="mt-2 uppercase tracking-widest font-mono text-[10px]">
                {user?.plan} · {user?.plan_status}
              </div>
            </div>
          </aside>

          <section className="min-h-[400px] min-w-0">
            {tab === "profile" && <ProfileTab user={user} refresh={refresh} />}
            {tab === "password" && <PasswordTab />}
            {tab === "billing" && <BillingTab user={user} />}
            {tab === "transactions" && <TransactionsTab />}
          </section>
        </div>
      </main>
    </div>
  );
}

/* -------------------------- Profile tab -------------------------- */
function ProfileTab({ user, refresh }) {
  const [name, setName] = useState(user?.name || "");
  const [username, setUsername] = useState(user?.username || "");
  const [busy, setBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const fileRef = React.useRef(null);

  useEffect(() => {
    setName(user?.name || "");
    setUsername(user?.username || "");
  }, [user?.name, user?.username]);

  async function save(e) {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name can't be empty.");
      return;
    }
    setBusy(true);
    try {
      const body = { name: name.trim() };
      if ((username || "").trim() && username.trim() !== (user?.username || "")) {
        body.username = username.trim();
      }
      await api.patch("/account/profile", body);
      await refresh();
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update profile.");
    } finally {
      setBusy(false);
    }
  }

  async function pickAvatar(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/image\/(png|jpeg|webp|gif)/.test(file.type)) {
      toast.error("Avatar must be a PNG, JPEG, WEBP or GIF.");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Avatar must be under 4 MB.");
      return;
    }
    setAvatarBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post("/account/avatar", form, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Avatar updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload avatar.");
    } finally {
      setAvatarBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function resetAvatar() {
    setAvatarBusy(true);
    try {
      await api.delete("/account/avatar");
      await refresh();
      toast.success("Reverted to the Draftwave avatar.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset avatar.");
    } finally {
      setAvatarBusy(false);
    }
  }

  return (
    <div data-testid="account-profile-panel">
      <SectionHeader title="Profile" subtitle="How Draftwave addresses you across the app." />

      {/* Avatar */}
      <div className="border border-border p-5 mb-8 max-w-lg" data-testid="avatar-panel">
        <div className="overline text-xs mb-4">Profile picture</div>
        <div className="flex items-center gap-5">
          <div className="shrink-0">
            <UserAvatar user={user} size={92} testid="avatar-preview" />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground mb-3">
              Upload a PNG, JPG, WEBP or GIF (max 4 MB). Skip to keep your one-of-a-kind Draftwave
              constellation.
            </p>
            <div className="flex flex-wrap gap-2">
              <label
                className={`inline-flex items-center gap-2 px-3 py-2 border border-border cursor-pointer overline text-xs hover-invert ${
                  avatarBusy ? "opacity-50 pointer-events-none" : ""
                }`}
                data-testid="avatar-upload-label"
              >
                {avatarBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                <span>{user?.picture ? "Replace" : "Upload"}</span>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={pickAvatar}
                  data-testid="avatar-file-input"
                />
              </label>
              {user?.picture && (
                <button
                  type="button"
                  onClick={resetAvatar}
                  disabled={avatarBusy}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-border overline text-xs hover-invert disabled:opacity-50"
                  data-testid="avatar-reset-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Reset</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={save} className="space-y-6 max-w-lg">
        <Field label="Full name" testid="profile-name-field">
          <input
            type="text"
            className="w-full px-4 py-3 border border-border bg-background text-foreground focus:outline-none focus:border-primary transition"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            data-testid="profile-name-input"
          />
        </Field>
        <Field
          label="Username"
          hint="3-24 characters: lowercase letters, numbers, underscore, dot or dash. This becomes your public @tag."
          testid="profile-username-field"
        >
          <div className="flex">
            <span className="inline-flex items-center px-3 border border-r-0 border-border bg-muted/40 text-muted-foreground overline text-xs">
              @
            </span>
            <input
              type="text"
              className="w-full px-4 py-3 border border-border bg-background text-foreground focus:outline-none focus:border-primary transition"
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase())}
              maxLength={24}
              placeholder="your_handle"
              data-testid="profile-username-input"
            />
          </div>
        </Field>
        <Field label="Email" hint="Contact support to change your email address." testid="profile-email-field">
          <input
            type="email"
            className="w-full px-4 py-3 border border-border bg-muted/30 text-muted-foreground cursor-not-allowed"
            value={user?.email || ""}
            readOnly
            data-testid="profile-email-input"
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <StatCard label="Credits" value={user?.credits ?? 0} testid="profile-credits" />
          <StatCard label="Plan" value={(user?.plan || "free").toUpperCase()} testid="profile-plan" />
        </div>
        <Button
          type="submit"
          disabled={busy}
          className="rounded-none border border-primary bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-6"
          data-testid="profile-save-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
          Save changes
        </Button>
      </form>
    </div>
  );
}

/* -------------------------- Password tab -------------------------- */
function PasswordTab() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [showCur, setShowCur] = useState(false);
  const [showNext, setShowNext] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/account/password", { current_password: cur, new_password: next });
      toast.success("Password updated.");
      setCur("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="account-password-panel">
      <SectionHeader title="Change password" subtitle="Use at least 8 characters. Mix cases and symbols." />
      <form onSubmit={submit} className="space-y-5 max-w-lg">
        <Field label="Current password" testid="pw-cur-field">
          <PasswordInput
            value={cur}
            onChange={setCur}
            show={showCur}
            toggle={() => setShowCur((v) => !v)}
            testid="pw-cur-input"
          />
        </Field>
        <Field label="New password" testid="pw-new-field">
          <PasswordInput
            value={next}
            onChange={setNext}
            show={showNext}
            toggle={() => setShowNext((v) => !v)}
            testid="pw-new-input"
          />
        </Field>
        <Field label="Confirm new password" testid="pw-confirm-field">
          <PasswordInput
            value={confirm}
            onChange={setConfirm}
            show={showNext}
            toggle={() => setShowNext((v) => !v)}
            testid="pw-confirm-input"
          />
        </Field>
        <Button
          type="submit"
          disabled={busy || !cur || !next || !confirm}
          className="rounded-none border border-primary bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-6"
          data-testid="pw-save-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
          Update password
        </Button>
      </form>
    </div>
  );
}

/* -------------------------- Billing tab -------------------------- */
function BillingTab({ user }) {
  const [busy, setBusy] = useState(false);

  async function openPortal() {
    setBusy(true);
    try {
      const r = await api.post("/account/billing-portal");
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Could not open billing portal.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Billing portal unavailable.");
    } finally {
      setBusy(false);
    }
  }

  const periodEnd = user?.current_period_end;
  return (
    <div data-testid="account-billing-panel">
      <SectionHeader
        title="Billing details"
        subtitle="Manage your payment method, billing address, tax IDs and invoices in Stripe's secure portal."
      />
      <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mb-8">
        <StatCard label="Plan" value={(user?.plan || "free").toUpperCase()} testid="billing-plan" />
        <StatCard label="Status" value={(user?.plan_status || "inactive").toUpperCase()} testid="billing-status" />
        <StatCard label="Credits" value={user?.credits ?? 0} testid="billing-credits" />
        <StatCard label="Renews" value={fmtDate(periodEnd) === "—" ? "—" : fmtDate(periodEnd).split(",")[0]} testid="billing-period" />
      </div>

      <div className="border border-border p-6 max-w-2xl">
        <div className="overline text-primary text-xs mb-2">Payment method & invoices</div>
        <h3 className="font-serif-display text-2xl mb-3">Open the Stripe portal</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Update your saved card, change your billing address, add a tax ID, download PDF invoices,
          and manage active subscriptions — all through Stripe's secure portal.
        </p>
        <Button
          onClick={openPortal}
          disabled={busy}
          className="rounded-none border border-primary bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-6"
          data-testid="billing-portal-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ExternalLink className="w-4 h-4 mr-2" />}
          Open billing portal
        </Button>
        <div className="mt-4 text-xs text-muted-foreground">
          Need to upgrade or top up credits?{" "}
          <Link to="/billing" className="underline text-foreground hover:text-primary" data-testid="billing-upgrade-link">
            Compare plans & credit packs →
          </Link>
        </div>
      </div>
    </div>
  );
}

/* -------------------------- Transactions tab -------------------------- */
function TransactionsTab() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/account/transactions?limit=100");
        if (mounted) setItems(r.data?.transactions || []);
      } catch (err) {
        if (mounted) toast.error("Couldn't load transactions.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const totals = useMemo(() => {
    if (!items) return { paid: 0, count: 0 };
    const paid = items.filter((t) => t.payment_status === "paid");
    const sum = paid.reduce((acc, t) => acc + Number(t.amount_cents || 0), 0);
    return { paid: sum, count: paid.length };
  }, [items]);

  return (
    <div data-testid="account-transactions-panel">
      <SectionHeader
        title="Transactions"
        subtitle="Every checkout, credit pack purchase, and subscription payment tied to your account."
      />
      <div className="grid sm:grid-cols-2 gap-4 max-w-lg mb-8">
        <StatCard label="Total paid" value={fmtAmount(totals.paid)} testid="txn-total-paid" />
        <StatCard label="Paid transactions" value={totals.count} testid="txn-count" />
      </div>

      {loading && (
        <div className="border border-border p-8 flex items-center gap-3 text-muted-foreground" data-testid="txn-loading">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="overline">Loading transactions…</span>
        </div>
      )}

      {!loading && items && items.length === 0 && (
        <div className="border border-border p-10 text-center" data-testid="txn-empty">
          <div className="overline text-muted-foreground mb-2">Nothing yet</div>
          <p className="text-sm text-muted-foreground">
            You haven't made any purchases yet.{" "}
            <Link to="/billing" className="underline text-foreground hover:text-primary">
              See plans →
            </Link>
          </p>
        </div>
      )}

      {!loading && items && items.length > 0 && (
        <div className="border border-border overflow-x-auto" data-testid="txn-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr className="text-left">
                <th className="px-4 py-3 overline text-xs font-normal">Date</th>
                <th className="px-4 py-3 overline text-xs font-normal">Item</th>
                <th className="px-4 py-3 overline text-xs font-normal">Amount</th>
                <th className="px-4 py-3 overline text-xs font-normal">Status</th>
                <th className="px-4 py-3 overline text-xs font-normal">Reference</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} className="border-t border-border" data-testid={`txn-row-${t.id}`}>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(t.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground">
                      {t.kind === "credit_pack"
                        ? `Credit pack · ${t.package_code || ""}`
                        : t.plan_code
                        ? `Plan · ${t.plan_code}`
                        : t.kind || "Purchase"}
                    </div>
                    {t.credits ? (
                      <div className="text-xs text-muted-foreground">{t.credits} credits</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 font-mono">{fmtAmount(t.amount_cents, t.currency)}</td>
                  <td className="px-4 py-3">
                    <StatusPill status={t.status} paymentStatus={t.payment_status} />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground truncate max-w-[220px]">
                    {t.session_id || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* -------------------------- Shared bits -------------------------- */
function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-8">
      <h2 className="font-serif-display text-3xl leading-tight">{title}</h2>
      {subtitle && <p className="mt-2 text-sm text-muted-foreground max-w-xl">{subtitle}</p>}
    </div>
  );
}

function Field({ label, hint, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="overline text-xs mb-2">{label}</div>
      {children}
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </label>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="border border-border p-4" data-testid={testid}>
      <div className="overline text-xs text-muted-foreground mb-1">{label}</div>
      <div className="font-serif-display text-2xl leading-none">{value}</div>
    </div>
  );
}

function PasswordInput({ value, onChange, show, toggle, testid }) {
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 pr-11 border border-border bg-background focus:outline-none focus:border-primary transition"
        autoComplete="off"
        data-testid={testid}
      />
      <button
        type="button"
        onClick={toggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        aria-label={show ? "Hide password" : "Show password"}
        data-testid={`${testid}-toggle`}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}
